/* eslint-disable */

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, IconButton } from "@mui/material";
import { saveAs } from "file-saver";
import MaterialReactTable from "material-react-table";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { SiteDTO, exportSites, uploadSites, exportRecsDetailBySite } from "api";
import { getMultipleRecDocumentDetails } from "api/rec";
import { PlusIcon, EsgExcelExport, EsgSearchIcon } from "assets";
import { COLORS, Loading, TextField } from "components";
import { RecUploadButton } from "components/buttons/rec-upload-button";
import { useGetAllowedValues, useNotify, useSites } from "hooks";
import { QUERY_KEYS } from "consts";
import { USER_ROLES, useGetSitesAllowedByUser, useNewBreadcrumb, useUserRole } from "recoils";
import { getSitesByStatus } from "utils";
import { SiteColumns } from "./components/site-editor-form/components/SiteColumns";
import { SITES_PAGE } from "./consts";
import { AddSiteModal } from "./components/site-editor-form/components/AddSiteModal";
import { getAllowedValues } from "./components";

export const TabSite = ({ state }: { state: string }) => {
  const notify = useNotify();
  const navigate = useNavigate();

  useNewBreadcrumb(SITES_PAGE.TITLE);

  const { data: sites, isLoading } = useSites();
  const { data: allowedValues } = useGetAllowedValues();

  const userRole = useUserRole();
  const sitesAllowed = useGetSitesAllowedByUser();
  const [isDataLoading, setIsLoading] = useState<boolean>(false);
  const [addSiteModalOpen, setAddSiteModalOpen] = useState(false);
  const activatedSites = getSitesByStatus({ sites, sitesAllowed, userRole, isActive: true });
  const deActivatedSites = getSitesByStatus({ sites, sitesAllowed, userRole, isActive: false });

  const currentSites = useMemo(() => state === "active" ? activatedSites : deActivatedSites, [sites, activatedSites, deActivatedSites, state]);

  const [searchValue, setSearchValue] = useState("");
  const [filterSites, setFilterSites] = useState<SiteDTO[]>(
    activatedSites || [],
  );

  const handleSearchChange = (event: any) => {
    const { value } = event.target;
    setSearchValue(value);
    handleSearch(value);
  };

  const handleCloseSiteModal = () => {
    if (addSiteModalOpen) {
      setAddSiteModalOpen(false);
    }
  };

  const handleNavigateToSiteDetails = (siteData: SiteDTO) => {
    if (siteData) {
      navigate(`/sites/site-details/${siteData.id}`);
    } else {
      notify.error("Site id not found.");
    }
  };

  const handleSearch = (value: string) => {
    const isActiveTab = state === 'active';
    const lowerValue = value.toLowerCase();

    const filteredSites = sites?.filter((site) => {
      if (site.enabled !== isActiveTab) return false;

      if (value === "") return true;

      return [site.name, site.type, site.city, site.country].some((field) =>
        field?.toLowerCase().includes(lowerValue)
      );
    }) || [];

    setFilterSites(filteredSites);
  };

  const exportToExcel = async () => {
    setIsLoading(true)
    notify.success("Data is being prepared, this might take a moment.");
    try {
      setIsLoading(true)
      const excelSites: any = await exportSites();
      const recs = await exportRecsDetailBySite();
      const siteSetup = excelSites.map((site: any) => ({
        "Site Name": site.name,
        "Customer Site ID": site.customerSiteId,
        "Business Unit": site.businessUnit,
        "Type of Site": site.type,
        State: site.status,
        Status: site.enabled ? "Active" : "Inactive",
        "Street address": site.address,
        City: site.city,
        Country: site.country,
        "Country Region": site.countryRegion,
        "Zip Code": site.zipCode,
        "Start Date": new Date(site.startDate),
        "End Date": new Date(site.endDate),
        Notes: site.notes,
      }));

      const utilityProviderInfo = excelSites.flatMap((site: any) =>
        site.collectors.map((collector: any) => ({
          "Site Name": site.name,
          "Customer Site ID": site.customerSiteId,
          "Collector ID": `${collector?.accountNumber}/${collector?.meterNumber}/${collector?.utilityTypeName}`,
          "Account Number": collector.accountNumber,
          "Meter Number": collector.meterNumber,
          "Utility Type": collector.utilityTypeName,
          utilitySubType: collector.utilitySubType,
          "Utility Provider": collector.providerName,
          Status: collector.enabled ? "Active" : "Inactive",
          "Document format": collector.allowedDocuments?.map((t: string) => t).join(", "),
          Frequency: collector.frequency,
          "Consumption Unit": collector.consumptionUnit,
          Currency: collector.currency,
          "GHG Emissions": collector?.scope,
          "Unit Cost": collector?.unitCost,
          "Spend Based": collector?.spendBased,
          "Start Date": new Date(collector?.startDate),
          "End Date": new Date(collector?.endDate),
        })),
      );

      const parameterBasedEstimatesInfo = excelSites.flatMap((site: any) =>
        site.estimates.map((estimate: any) => ({
          "Site Name": estimate.siteName,
          "Customer Site ID": site.customerSiteId,
          "Estimate Name": estimate.estimateName,
          "Utility Type": estimate.utilityName,
          "Estimate Type": estimate.estimateType,
          "Estimation Value": estimate.estimateHeadCount,
          "Estimated Usage per Day": estimate.estimateUsage,
          "Consumption Unit": estimate.estimateUsageUnit,
          "Start Date": estimate.estimateStartDate,
          "End Date": estimate.estimateEndDate,
          "GHG Emission": estimate.scope,
          "Notes": estimate.notes,
        })),
      );

      const dataProvidersInfo = excelSites.flatMap((site: any) =>
        site.users.map((user: any) => ({
          "Site Name": site.name,
          "Customer Site ID": site.customerSiteId,
          "Data manager": `${user.firstName} ${user.lastName}`,
          Email: user.email,
        })),
      );

      const renewableEnergyCertificatesInfo: any = [];

      const fetchRECDetails = async (recId: any) => {
        if (recId?.length > 0) {
          const recResponse = await getMultipleRecDocumentDetails(recId);
          return recResponse.data;
        }
        return null;
      };

      const formatCollectorString = (collectorRecsDtoList: any) =>
        collectorRecsDtoList
          .map(
            ({ siteName, meterNumber = "N/A", accountNumber = "N/A" }: { siteName: string, meterNumber: string, accountNumber: string }) =>
              `${siteName} - ${meterNumber} - ${accountNumber}`
          )
          .join("\n");

      const recCache = new Map();

      const getRecData = async (recId: any[] | undefined) => {
        if (recCache.has(recId)) {
          return recCache.get(recId);
        }
        const data = await fetchRECDetails(recId);
        recCache.set(recId, data);
        return data;
      }

      const recIds = excelSites?.flatMap((item: any) =>
        item.collectors
          .filter((collector: any) => collector.recsDetail && collector.recsDetail.recId !== undefined)
          .map((collector: any) => collector.recsDetail.recId)
      );

      const recData = await getRecData(recIds);

      recData?.forEach((recItem: any) => {
        let matchingScope: string | null = null;

        excelSites?.forEach((site: any) => {
          if (matchingScope) return;
          const relevantCollectors = site.collectors.filter((collector: any) => collector.recs);
          relevantCollectors.forEach(({ scope }: { scope: any }) => {
            if (scope && !matchingScope) {
              matchingScope = scope;
            }
          });
        });

        renewableEnergyCertificatesInfo.push({
          "Utility Provider": recItem.utilityProvider,
          "Utility Type": recItem.utilityTypeName,
          "Start Date": recItem.startDate,
          "End Date": recItem.endDate,
          Scope: matchingScope,
          Collector: formatCollectorString(recItem.collectorRecsDtoList),
        });
      });

      const recDetailsBySite = recs?.flatMap((currentRecData: any) => ({
        "Site Name": currentRecData.siteName,
        "Customer Site ID": currentRecData.customerSiteId,
        "REC Country": currentRecData.country,
        "REC Provider": currentRecData.recsProvider,
        "REC Utility Type": currentRecData.recsUtilityType,
        "REC Start Date": dayjs(currentRecData.recsStartDate).format("YYYY-MM-DD"),
        "REC End Date": dayjs(currentRecData.recsEndDate).format("YYYY-MM-DD"),
        "REC Percentage": currentRecData.recsPercentage,
        "Collector": currentRecData.collector?.replace("==>", "; "),
        "Scope of Collector": currentRecData.scope,
        "Utility Type of Collector": currentRecData.utilityTypeOfCollector
      }));

      const wb = XLSX.utils.book_new();

      const wsSiteSetup = XLSX.utils.json_to_sheet(siteSetup);

      const range = XLSX.utils.decode_range(wsSiteSetup['!ref'] as string);
      for (let row = range.s.r + 1; row <= range.e.r; row += 1) {
        const startDateCell = XLSX.utils.encode_cell({ r: row, c: 12 });
        if (wsSiteSetup[startDateCell]) {
          wsSiteSetup[startDateCell].z = 'yyyy-mm-dd';
        }

        const endDateCell = XLSX.utils.encode_cell({ r: row, c: 13 });
        if (wsSiteSetup[endDateCell]) {
          wsSiteSetup[endDateCell].z = 'yyyy-mm-dd';
        }
      }

      const wsUtilityProviderInfo = XLSX.utils.json_to_sheet(utilityProviderInfo);
      const wsParameterBasedEstimatesInfo = XLSX.utils.json_to_sheet(
        parameterBasedEstimatesInfo,
      );
      const wsDataProvidersInfo = XLSX.utils.json_to_sheet(dataProvidersInfo);
      const wsRenewableEnergyInfo = XLSX.utils.json_to_sheet(
        renewableEnergyCertificatesInfo,
      );

      const wsRecDetailsBySite = XLSX.utils.json_to_sheet(recDetailsBySite);

      XLSX.utils.book_append_sheet(wb, wsSiteSetup, "Site Setup");
      XLSX.utils.book_append_sheet(
        wb,
        wsUtilityProviderInfo,
        "Utility Provider Info",
      );
      XLSX.utils.book_append_sheet(
        wb,
        wsParameterBasedEstimatesInfo,
        "Parameter Estimates Setup",
      );
      XLSX.utils.book_append_sheet(
        wb,
        wsDataProvidersInfo,
        "Data Providers Info",
      );
      XLSX.utils.book_append_sheet(wb, wsRenewableEnergyInfo, "RECS Info");
      XLSX.utils.book_append_sheet(wb, wsRecDetailsBySite, "REC details by site");

      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary", cellDates: true });

      const s2ab = (s: any) => {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i < s.length; i += 1) {
          view[i] = s.charCodeAt(i) & 0xff; // eslint-disable-line no-bitwise
        }
        return buf;
      };

      saveAs(
        new Blob([s2ab(wbout)], { type: "application/octet-stream" }),
        "Site_Management_Data.xlsx",
      );
    } catch (e) {
      notify.error("Failed to Export to Excel");
      setIsLoading(false)
    }
    setIsLoading(false)
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2, mb: 2 }}>
            <RecUploadButton
              label="Upload Data"
              uploadApiCall={uploadSites}
              queryKeyToInvalidate={QUERY_KEYS.SITES}
              availableToRoles={[USER_ROLES.MODULE_ADMIN, USER_ROLES.DATA_PROVIDER, USER_ROLES.DATA_VALIDATOR, USER_ROLES.DATA_REVIEWER]}
              extraReqParams={[["configuration", "true"]]}
            />
            <Button
              sx={{ height: "36px", width: "140px", borderRadius: 2, marginTop: "10px" }}
              variant="contained"
              onClick={() => setAddSiteModalOpen(true)}
              startIcon={<PlusIcon />}
              disabled={userRole.isAuditor || userRole.isDataProvider}
            >
              Add Site
            </Button>
            <Button
              sx={{ height: "36px", width: "200px", borderRadius: 2, marginTop: "10px" }}
              variant="contained"
              onClick={exportToExcel}
              startIcon={<EsgExcelExport />}
              disabled={isDataLoading}
            >
              {isDataLoading ? "Loading..." : "Export to Excel"}
            </Button>
          </Box>
        </Box>
        <Box
          display="flex"
          gap="2"
          justifyContent="flex-end"
          alignItems="center"
          mb={2}
          sx={{
            width: "240px",
            height: "36px",
            borderRadius: 2,
            border: "1px solid  black",
            marginTop: "7px",
          }}
        >
          <IconButton>
            <EsgSearchIcon />
          </IconButton>
          <TextField
            placeholder="Search sites..."
            value={searchValue}
            onChange={handleSearchChange}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              border: "none",
              "& input": {
                padding: 0,
                fontSize: "14px",
              },
              "& fieldset": {
                border: "none",
              }
            }}
          />
        </Box>
      </Box>
      {isLoading ? (
        <Loading />
      ) : (
        <MaterialReactTable
          state={{ isLoading }}
          enableSorting
          columns={SiteColumns}
          enableGlobalFilter={false}
          data={searchValue.length > 0 ? filterSites : currentSites!}
          enablePagination
          muiTableContainerProps={{
            sx: {
              "& thead": {
                position: "sticky",
                top: 0,
                zIndex: 9,
              },
              "& thead tr:first-of-type th:first-of-type > div > div > div": {
                minWidth: "36px",
                width: "36px",
              },
              "& thead tr:first-of-type th:nth-child(2)": {
                minWidth: "160px",
                width: "160px",
              },
              "& thead tr:first-of-type th:nth-child(4)": {
                minWidth: "190px",
                width: "190px",
              },
              "& thead tr:first-of-type th:nth-child(5)": {
                minWidth: "140px",
                width: "140px",
              },
              "& tbody tr:nth-child(even)": {
                height: 0,
              },
              "& tbody": {
                maxWidth: '100%',
                "& tr:nth-child(even) > td:first-of-type > div: first-of-type > div > div": {
                  width: 'calc(100vw - 200px)',
                },
              },
              "& tbody tr td:first-of-type": {
                paddingLeft: "20px",
              },
              "& tbody tr:first-of-type td > p": {
                display: "flex",
                width: "100%",
                justifyContent: "center",
              },
              height: "calc(100vh - 300px)",
              border: "1px solid #D9D9D9",
              borderRadius: "10px",
              minWidth: "100%",
              width: "100%",
              maxWidth: "100%",
              "@media (min-width: 600px)": {
                width: "100%",
              },
              "@media (min-width: 960px)": {
                width: "1100px",
              },
              "@media (min-width: 1280px)": {
                width: "1100px",
              },
              "@media (min-width: 1440px)": {
                width: "1100px",
              },
              "@media (min-width: 1680px)": {
                width: "1300px",
              },
              "@media (min-width: 2400px)": {
                width: "100%",
              },
            },
          }}
          muiTablePaperProps={{
            sx: {
              borderRadius: "10px",
              boxShadow: "none",
            },
          }}
          muiTopToolbarProps={{
            sx: {
              display: "none",
            },
          }}
          muiBottomToolbarProps={{
            sx: {
              display: "none",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              color: COLORS.Mainblue,
              lineHeight: "30px",
              "& .MuiBadge-root": {
                display: "none",
              },
            },
          }}
          muiTableHeadRowProps={{
            sx: {
              height: "45px",
              backgroundColor: "#F8FAFC",
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => handleNavigateToSiteDetails(row.original),
            sx: {
              height: "45px",
              cursor: 'pointer',
              backgroundColor: "white",
              minWidth: '100%',
              width: '100%',
              maxWidth: '100%',
              '@media (min-width: 600px)': {
                width: '100%',
              },
              '@media (min-width: 960px)': {
                width: '1180px',

              },
              '@media (min-width: 1280px)': {
                width: '1140px',

              },
              '@media (min-width: 1440px)': {
                width: '1290px',

              },
              '@media (min-width: 1680px)': {
                width: '1540px',

              },
              '@media (min-width: 2400px)': {
                width: '100%',

              },
            },
          })}
          positionActionsColumn="last"
          defaultColumn={{
            minSize: 20,
            maxSize: 9001,
            size: 320,
          }}
          muiTablePaginationProps={{
            rowsPerPageOptions: [100, 200, 500, 1000],
            showFirstButton: true,
            showLastButton: true,
          }}
          initialState={{
            pagination: { pageSize: 100, pageIndex: 0 },
          }}
        />
      )}
      <AddSiteModal
        isOpen={addSiteModalOpen}
        onClose={handleCloseSiteModal}
        allowedValues={getAllowedValues(allowedValues)}
      />
    </Box>
  );
};
